import { Search, UserMinus, UserPlus } from 'lucide-react';
import { useCallback, useEffect, useState } from 'react';
import {
  PERMISSIONS,
  ROLE_LABELS,
  ROLE_PRESETS,
  hasPermission,
  type MemberRole,
  type Permission,
} from '@hyphen-db-studio/access-core';
import { api } from '../../lib/api';
import { useAuthStore } from '../../store/useAuthStore';
import { useWorkspaceStore, type Organization } from '../../store/useWorkspaceStore';
import PermissionEditor, { type Grant } from './PermissionEditor';

interface Member {
  userId: string;
  username: string;
  email: string;
  role: MemberRole;
  permissions: Permission[];
}

interface Candidate {
  id: string;
  username: string;
  email: string;
}

const errorMessage = (err: unknown) => (err instanceof Error ? err.message : 'Something went wrong');

function Avatar({ name }: { name: string }) {
  return (
    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-sky-500 to-cyan-400 text-sm font-semibold uppercase text-white">
      {name.charAt(0)}
    </span>
  );
}

function AddMember({ org, grantable, onAdded }: { org: Organization; grantable: readonly Permission[]; onAdded: () => void }) {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState<Candidate[]>([]);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState<Candidate | null>(null);
  const [grant, setGrant] = useState<Grant>({ role: 'viewer', permissions: [] });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (selected) return;
    const q = query.trim();
    if (q.length < 2) {
      setResults([]);
      return;
    }
    setSearching(true);
    const timer = setTimeout(() => {
      api
        .get<Candidate[]>(`/api/orgs/${org.id}/member-search?q=${encodeURIComponent(q)}`)
        .then(setResults)
        .catch((err) => setError(errorMessage(err)))
        .finally(() => setSearching(false));
    }, 250);
    return () => clearTimeout(timer);
  }, [query, selected, org.id]);

  async function add() {
    if (!selected) return;
    setBusy(true);
    setError(null);
    try {
      await api.post(`/api/orgs/${org.id}/members`, { userId: selected.id, role: grant.role, permissions: grant.permissions });
      setSelected(null);
      setQuery('');
      setResults([]);
      setGrant({ role: 'viewer', permissions: [] });
      onAdded();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="surface mb-6 p-5">
      <h3 className="mb-3 flex items-center gap-2 text-sm font-semibold dark:text-gray-100">
        <UserPlus className="h-4 w-4 text-sky-600 dark:text-sky-400" /> Add a member
      </h3>
      {!selected ? (
        <>
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
            <input
              value={query}
              onChange={(e) => {
                setQuery(e.target.value);
                setError(null);
              }}
              placeholder="Search by username or email"
              className="w-full rounded-lg border border-gray-300 bg-white py-2.5 pl-9 pr-3 text-sm shadow-sm placeholder:text-gray-400 focus:border-sky-500 focus:ring-4 focus:ring-sky-500/15 dark:border-white/15 dark:bg-gray-800 dark:text-gray-100"
            />
          </div>
          {query.trim().length >= 2 && !searching && results.length === 0 && (
            <p className="mt-3 text-sm text-gray-500 dark:text-gray-400">No matching users who aren't already members.</p>
          )}
          {results.length > 0 && (
            <ul className="mt-3 divide-y divide-gray-100 rounded-lg border border-gray-200 dark:divide-white/10 dark:border-white/10">
              {results.map((c) => (
                <li key={c.id} className="flex items-center gap-3 px-3 py-2">
                  <Avatar name={c.username} />
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium dark:text-gray-100">{c.username}</div>
                    <div className="truncate text-xs text-gray-500 dark:text-gray-400">{c.email}</div>
                  </div>
                  <button onClick={() => setSelected(c)} className="btn-ghost !py-1.5 text-sky-600 dark:text-sky-300">
                    Select
                  </button>
                </li>
              ))}
            </ul>
          )}
        </>
      ) : (
        <div>
          <div className="mb-4 flex items-center gap-3">
            <Avatar name={selected.username} />
            <div className="min-w-0 flex-1">
              <div className="truncate text-sm font-medium dark:text-gray-100">{selected.username}</div>
              <div className="truncate text-xs text-gray-500 dark:text-gray-400">{selected.email}</div>
            </div>
            <button onClick={() => setSelected(null)} className="btn-ghost !py-1.5">
              Change
            </button>
          </div>
          <PermissionEditor value={grant} onChange={setGrant} grantable={grantable} />
          <div className="mt-4 flex justify-end">
            <button onClick={add} disabled={busy} className="btn-primary">
              {busy ? 'Adding…' : `Add ${selected.username}`}
            </button>
          </div>
        </div>
      )}
      {error && (
        <div role="alert" className="mt-3 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
    </div>
  );
}

function MemberRow({
  org,
  member,
  canManage,
  grantable,
  isSelf,
  onChanged,
}: {
  org: Organization;
  member: Member;
  canManage: boolean;
  grantable: readonly Permission[];
  isSelf: boolean;
  onChanged: () => void;
}) {
  const leaveOrganization = useWorkspaceStore((s) => s.leaveOrganization);
  const [editing, setEditing] = useState(false);
  const [grant, setGrant] = useState<Grant>({
    role: member.role === 'owner' ? 'admin' : member.role,
    permissions: member.permissions,
  });
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isOwner = member.role === 'owner';
  const summary = isOwner
    ? 'Full access'
    : member.permissions.length === 0
      ? 'View only'
      : member.permissions.map((p) => p.split('.').join(' ')).join(' · ');

  async function run(action: () => Promise<unknown>) {
    setBusy(true);
    setError(null);
    try {
      await action();
    } catch (err) {
      setError(errorMessage(err));
      setBusy(false);
      return;
    }
    setBusy(false);
  }

  const save = () =>
    run(async () => {
      await api.patch(`/api/orgs/${org.id}/members/${member.userId}`, { role: grant.role, permissions: grant.permissions });
      setEditing(false);
      onChanged();
    });

  const remove = () => {
    if (!confirm(`Remove ${member.username} from ${org.name}?`)) return;
    return run(async () => {
      await api.delete(`/api/orgs/${org.id}/members/${member.userId}`);
      onChanged();
    });
  };

  const leave = () => {
    if (!confirm(`Leave ${org.name}? You will lose access to its projects.`)) return;
    return run(async () => {
      await leaveOrganization(org.id, member.userId);
      window.location.hash = '#/';
    });
  };

  return (
    <li className="px-4 py-3">
      <div className="flex items-center gap-3">
        <Avatar name={member.username} />
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <span className="truncate text-sm font-medium dark:text-gray-100">{member.username}</span>
            {isSelf && <span className="text-xs text-gray-400">(you)</span>}
            <span
              className={`shrink-0 rounded-full px-2 py-0.5 text-[11px] font-medium ${
                isOwner ? 'bg-amber-100 text-amber-700 dark:bg-amber-500/15 dark:text-amber-300' : 'bg-gray-100 text-gray-600 dark:bg-white/10 dark:text-gray-300'
              }`}
            >
              {ROLE_LABELS[member.role]}
            </span>
          </div>
          <div className="truncate text-xs text-gray-500 dark:text-gray-400">
            {member.email} · {summary}
          </div>
        </div>
        {canManage && !isOwner && (
          <>
            <button onClick={() => setEditing((v) => !v)} className="btn-ghost !py-1.5">
              {editing ? 'Close' : 'Permissions'}
            </button>
            <button onClick={remove} disabled={busy} className="rounded-lg p-2 text-gray-400 hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-500/10 dark:hover:text-red-400" title="Remove member">
              <UserMinus className="h-4 w-4" />
            </button>
          </>
        )}
        {isSelf && !isOwner && !canManage && (
          <button onClick={leave} disabled={busy} className="btn-ghost !py-1.5 text-red-600 dark:text-red-400">
            Leave
          </button>
        )}
      </div>
      {editing && (
        <div className="mt-4 rounded-xl bg-gray-50 p-4 dark:bg-white/5">
          <PermissionEditor value={grant} onChange={setGrant} grantable={grantable} />
          <div className="mt-4 flex justify-end gap-2">
            <button onClick={() => setEditing(false)} className="btn-ghost">
              Cancel
            </button>
            <button onClick={save} disabled={busy} className="btn-primary">
              {busy ? 'Saving…' : 'Save permissions'}
            </button>
          </div>
        </div>
      )}
      {error && (
        <div role="alert" className="mt-2 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">
          {error}
        </div>
      )}
    </li>
  );
}

export default function MembersPanel({ org }: { org: Organization }) {
  const user = useAuthStore((s) => s.user);
  const [members, setMembers] = useState<Member[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const canManage = hasPermission(org, 'members.manage');
  // Non-owners can only hand out permissions they hold themselves.
  const grantable: readonly Permission[] = org.role === 'owner' ? PERMISSIONS : org.permissions;

  const refresh = useCallback(() => {
    api
      .get<Member[]>(`/api/orgs/${org.id}/members`)
      .then((m) => {
        setMembers(m);
        setError(null);
      })
      .catch((err) => setError(errorMessage(err)));
  }, [org.id]);

  useEffect(refresh, [refresh]);

  return (
    <div>
      {canManage && <AddMember org={org} grantable={grantable} onAdded={refresh} />}
      {error && <div className="mb-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700 dark:bg-red-500/10 dark:text-red-300">{error}</div>}
      <div className="surface">
        <div className="border-b border-gray-100 px-4 py-3 text-sm font-semibold dark:border-white/10 dark:text-gray-100">
          Members {members && <span className="ml-1 font-normal text-gray-400">{members.length}</span>}
        </div>
        {members === null ? (
          <div className="px-4 py-6 text-sm text-gray-500 dark:text-gray-400">Loading members…</div>
        ) : (
          <ul className="divide-y divide-gray-100 dark:divide-white/10">
            {members.map((m) => (
              <MemberRow
                key={m.userId}
                org={org}
                member={m}
                canManage={canManage}
                grantable={grantable}
                isSelf={m.userId === user?.id}
                onChanged={refresh}
              />
            ))}
          </ul>
        )}
      </div>
      {!canManage && (
        <p className="mt-4 text-sm text-gray-500 dark:text-gray-400">Only members with the "Manage members" permission can add people or change access.</p>
      )}
    </div>
  );
}
